import Slider from '@mui/material/Slider';
import type { FC } from 'react';
import { useCallback, useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { theme } from '../../styles/theme';

import type { RangeFilterConfig } from './filter-types';
import { NestedFilterOption } from './FilterOption';
import { useFilterItemStyles } from './use-filter-item-styles';

interface Props {
  rangeFilterConfig: RangeFilterConfig;
  rangeStart?: number;
  rangeFinish?: number;
}

export const RangeFilter: FC<Props> = ({
  rangeFilterConfig,
  rangeStart,
  rangeFinish,
}) => {
  const { icon, title, key, rangeFrom, rangeTo, onUpdate, fixedStart } =
    rangeFilterConfig;

  const [selectedRange, setSelectedRange] = useState([
    rangeStart ?? rangeFrom.min,
    rangeFinish ?? rangeTo.max,
  ]);
  const [isInDebounce, setIsInDebounce] = useState(false);

  useEffect(() => {
    const rangeHasBeenFiltered = fixedStart
      ? selectedRange[1] !== rangeTo.max
      : selectedRange[0] !== rangeFrom.min || selectedRange[1] !== rangeTo.max;

    const shouldReset = fixedStart
      ? rangeFinish === undefined && rangeHasBeenFiltered && !isInDebounce
      : rangeStart === undefined &&
        rangeFinish === undefined &&
        rangeHasBeenFiltered &&
        !isInDebounce;

    if (shouldReset) {
      setSelectedRange([rangeFrom.min, rangeTo.max]);
    }
  }, [
    rangeFinish,
    isInDebounce,
    rangeFrom.min,
    rangeTo.max,
    selectedRange,
    rangeStart,
    fixedStart,
  ]);

  const classes = useFilterItemStyles();
  const marks = [
    {
      value: rangeFrom.min,
      label: `${rangeFrom.min}`,
    },

    {
      value: rangeTo.max,
      label: `${rangeTo.max}`,
    },
  ];

  const debouncedUpdateFilterOptions = useDebouncedCallback(
    (updatedFilters: Record<string, string>) => {
      onUpdate(updatedFilters);
      setIsInDebounce(false);
    },
    300,
  );

  const handleOnChange = useCallback(
    (_: Event, newValue: number | number[], activeThumb: number) => {
      if (!Array.isArray(newValue)) return;
      setIsInDebounce(true);

      if (fixedStart && activeThumb === 0) {
        return;
      }

      if (fixedStart) {
        debouncedUpdateFilterOptions({
          [rangeTo.key]: String(newValue[1]),
        });
        setSelectedRange([rangeFrom.min, newValue[1]]);
      } else {
        const minDistanceApart = 5;
        if (newValue[1] - newValue[0] < minDistanceApart) {
          if (activeThumb === 0) {
            const clamped = Math.min(
              newValue[0],
              rangeTo.max - minDistanceApart,
            );
            const clampedLeftValue = [clamped, clamped + minDistanceApart];
            debouncedUpdateFilterOptions({
              [rangeFrom.key]: String(clampedLeftValue[0]),
              [rangeTo.key]: String(clampedLeftValue[1]),
            });
            setSelectedRange(clampedLeftValue as [number, number]);
          } else {
            const clamped = Math.max(
              newValue[1],
              minDistanceApart + rangeFrom.min,
            );
            const clampedRightValue = [clamped - minDistanceApart, clamped];
            debouncedUpdateFilterOptions({
              [rangeFrom.key]: String(clampedRightValue[0]),
              [rangeTo.key]: String(clampedRightValue[1]),
            });
            setSelectedRange(clampedRightValue as [number, number]);
          }
        } else {
          debouncedUpdateFilterOptions({
            [rangeFrom.key]: String(newValue[0]),
            [rangeTo.key]: String(newValue[1]),
          });
          setSelectedRange(newValue as [number, number]);
        }
      }
    },
    [
      debouncedUpdateFilterOptions,
      fixedStart,
      rangeFrom.key,
      rangeFrom.min,
      rangeTo.key,
      rangeTo.max,
    ],
  );

  return (
    <div className={classes.item} key={title + key}>
      <NestedFilterOption icon={icon} title={title}>
        <Slider
          getAriaLabel={() => 'Year range'}
          getAriaValueText={String}
          sx={{
            width: theme.spacing(64),
            marginLeft: theme.spacing(3),
            marginRight: theme.spacing(6),
            height: theme.spacing(1), // 4px
            padding: `${theme.spacing(3)} 0`, // 12px
            marginBottom: theme.spacing(5), // 20px
            '@media (pointer: coarse)': {
              padding: `${theme.spacing(5)} 0`, // 20px
            },
            ...(fixedStart && {
              '& .MuiSlider-thumb[data-index="0"]': {
                display: 'none',
              },
            }),
          }}
          min={rangeFrom.min}
          max={rangeTo.max}
          value={selectedRange}
          onChange={handleOnChange}
          marks={marks}
          step={1}
          valueLabelDisplay="auto"
          disableSwap
        />
      </NestedFilterOption>
    </div>
  );
};
